import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';

import { api } from '@/lib/api';
import { CountryFlag } from '@/components/CountrySelect';
import type { PublicUser } from '@shared/types';
import { Avatar } from '@/components/Avatar';

interface SearchResult extends PublicUser {
  rating: number | null;
}

interface Props {
  onClose: () => void;
}

/** Buscador de jugadores, al estilo de una paleta de comandos. */
export function SearchPalette({ onClose }: Props) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Búsqueda con retardo: no se consulta en cada tecla.
  useEffect(() => {
    const query = term.trim();
    if (query.length < 1) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const controller = new AbortController();
    const timer = setTimeout(() => {
      api
        .get<{ items: SearchResult[] }>('/users/search', { q: query }, controller.signal)
        .then((data) => {
          setResults(data.items);
          setCursor(0);
        })
        .catch(() => undefined)
        .finally(() => setLoading(false));
    }, 180);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [term]);

  const open = (username: string) => {
    navigate(`/@/${username}`);
    onClose();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((c) => Math.min(c + 1, results.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (event.key === 'Enter' && results[cursor]) {
      event.preventDefault();
      open(results[cursor].username);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 px-4 pt-[12vh] backdrop-blur-md"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Buscar jugadores"
    >
      <div className="animate-pop-in w-full max-w-xl overflow-hidden rounded-2xl border border-white/15 bg-[#0e111a]/98 shadow-[0_24px_70px_rgba(0,0,0,0.85)] backdrop-blur-2xl">
        <div className="flex items-center gap-3 border-b border-white/10 px-4 py-1">
          <Search size={18} className="shrink-0 text-blue-400" />
          <input
            ref={inputRef}
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Buscar jugadores por nombre…"
            className="w-full bg-transparent py-3.5 text-sm text-white placeholder:text-white/40 outline-none"
            aria-label="Nombre de jugador"
            autoComplete="off"
          />
          <button
            type="button"
            className="rounded-lg p-1.5 text-white/40 transition-colors hover:bg-white/10 hover:text-white"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[50vh] overflow-y-auto">
          {term.trim() === '' ? (
            <p className="px-4 py-8 text-center text-sm text-white/40">
              Escribe para encontrar a otros jugadores.
            </p>
          ) : loading && results.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-white/40">Buscando…</p>
          ) : results.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-white/40">
              Ningún jugador coincide con «{term.trim()}».
            </p>
          ) : (
            <ul role="listbox">
              {results.map((user, index) => (
                <li key={user.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={index === cursor}
                    onMouseEnter={() => setCursor(index)}
                    onClick={() => open(user.username)}
                    className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                      index === cursor ? 'bg-white/[0.08] text-white' : 'text-white/80 hover:bg-white/[0.04]'
                    }`}
                  >
                    <span className="text-xl leading-none" aria-hidden="true"><Avatar value={user.avatar} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        {user.title ? (
                          <span className="title-badge" data-bot={user.title === 'BOT'}>
                            {user.title}
                          </span>
                        ) : null}
                        <span className="truncate-1 font-medium">{user.username}</span>
                        {user.country ? (
                          <CountryFlag code={user.country} className="text-xs" />
                        ) : null}
                      </span>
                    </span>
                    {user.isOnline ? (
                      <span
                        className="size-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)]"
                        title="En línea"
                        aria-label="En línea"
                      />
                    ) : null}
                    {user.rating ? (
                      <span className="font-mono text-sm text-white/50">{user.rating}</span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
